import React from 'react';
import { useDispatch } from 'react-redux';
import { eventSelectScreenSelection, NAVIGATE_MODAL } from 'store/actions';
import { ModalScreens } from 'components/Navigation/types';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { ICONS } from 'components/Icon';
import { randomKey } from 'lib/riskbase';
import { Button } from 'components/Button';
import { pluralise } from 'lib/pluralise';
import { Menu, MenuProps } from 'components/Menu';

type Option<K extends string> = {
  name: string;
  subname?: string;
  value: K;
  groupName?: string;
  icon?: ICONS | undefined;
  iconColor?: string | undefined;
};

type Props<K extends string> = {
  labelNone: string;
  labelAll: string;
  labelMultiple: string;
  options: Option<K>[];
  values: string[];
  enableSelectAll: boolean;
  //
  multiSelect?: boolean;
  buttonMaxWidth?: number;
  menuWidth?: number;
  modal?: true;
  modalSize?: 'small' | 'fullscreen' | 'large';
  modalTitle?: string;
  modalSearchable?: boolean;
  modalEnableSelectAll?: boolean;
  noValueOption?: {
    name: string;
    subname?: string;
    groupName?: string;
    icon?: ICONS | undefined;
    iconColor?: string | undefined;
  };
  showBadgeWhenValue?: boolean;
  //
  onChange: (value: K[]) => void;
};

export const QuickOptionField = <K extends string>({
  options,
  values,
  //
  labelAll,
  labelMultiple,
  labelNone,
  enableSelectAll: enableAllButton,
  //
  multiSelect,
  buttonMaxWidth,
  menuWidth,
  modal,
  modalSize,
  modalTitle,
  modalSearchable,
  modalEnableSelectAll,
  noValueOption,
  showBadgeWhenValue,
  //
  onChange,
}: Props<K>): React.ReactElement => {
  const dispatch = useDispatch();
  const selectKeyRef = React.useRef(randomKey());

  const filteredOptions = values.length
    ? options.filter((item) => values.includes(item.value))
    : [];

  const buttonText = !filteredOptions.length
    ? labelNone
    : filteredOptions.length === 1
      ? filteredOptions[0]?.name
      : filteredOptions.length === options.length
        ? labelAll
        : pluralise(filteredOptions.length, labelMultiple, labelMultiple);

  const menuOptions: MenuProps['options'] = options.map((option) => ({
    text: option.name,
    subText: option.subname,
    icon: option.icon, // ? (ICONS[option.icon] as unknown as ICONS) : undefined,
    iconColor: option.iconColor,
    onPress: () => onChange([option.value]),
  }));
  if (enableAllButton) {
    menuOptions.push({
      text: noValueOption?.name || labelAll,
      subText: noValueOption?.subname,
      onPress: () => onChange([]),
      icon: noValueOption?.icon,
      iconColor: noValueOption?.iconColor,
    });
  }

  const handleShowSelectModal = React.useCallback(() => {
    const selectOptions = options.map((option) => ({
      groupName: option.groupName, // || (multiSelect ? 'Choose One or More' : 'Choose One'),
      name: option.name,
      // value: option.value,
      internalValue: option.value,
      icon: option.icon,
      iconColor: option.iconColor,
    }));
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        size: modalSize,
        params: {
          title:
            modalTitle || (multiSelect ? 'Choose One or More' : 'Choose One'),
          searchable: modalSearchable,
          enableSelectAll: modalEnableSelectAll,
          eventKey: selectKeyRef.current,
          multiSelect,
          options: enableAllButton
            ? [
                {
                  name: noValueOption?.name || labelAll,
                  // value: '',
                  groupName: noValueOption?.groupName,
                  internalValue: undefined,
                },
                ...selectOptions,
              ]
            : selectOptions,
          value: values,
        },
      }),
    );
  }, [
    dispatch,
    enableAllButton,
    labelAll,
    modalEnableSelectAll,
    modalSearchable,
    modalSize,
    modalTitle,
    multiSelect,
    noValueOption?.groupName,
    noValueOption?.name,
    options,
    values,
  ]);

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === selectKeyRef.current) {
          onChange(event.payload.stringValues as K[]);
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [onChange]);

  if (modal || multiSelect) {
    return (
      <Button
        text={buttonText}
        style={buttonMaxWidth ? { maxWidth: buttonMaxWidth } : undefined}
        onPress={handleShowSelectModal}
        showBadge={showBadgeWhenValue && !!filteredOptions.length}
      />
    );
  }

  return (
    <Menu
      buttonConfig={{
        text: buttonText,
        style: { maxWidth: buttonMaxWidth },
        showBadge: showBadgeWhenValue && !!filteredOptions.length,
      }}
      width={menuWidth}
      options={menuOptions}
    />
  );
};
